import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { ChartBucket, ChartPreset, TicketSalesInterval } from '@/gql/graphql';
import type { ChartKind } from './TicketSalesChart';

/** The Preset select also offers Ticket sales, which is not a `ChartPreset` (it has its own queries). */
export type PresetChoice = ChartPreset | 'TICKET_SALES';

/** Every `ChartPreset`; the `Record` makes the compiler flag a preset added to the schema but not here. */
const CHART_PRESET_SET: Record<ChartPreset, true> = {
  TOTAL_SALES: true,
  SALES_PER_DEVICE: true,
  TRANSACTIONS_PER_DEVICE: true,
  SALES_PER_CATEGORY: true,
  SALES_PER_PAYMENT_METHOD: true,
  TRANSACTIONS_PER_PAYMENT_METHOD: true,
  AVERAGE_TRANSACTION_VALUE: true,
  TIPS: true,
  SALES_PER_PRODUCT: true,
  ENTRANCE_SCANS: true,
};

const BUCKETS: Record<ChartBucket, true> = { TWO_MIN: true, FIVE_MIN: true, TEN_MIN: true, THIRTY_MIN: true };
const GROUPINGS: Record<TicketSalesInterval, true> = { HOUR: true, DAY: true, WEEK: true };
const KINDS: Record<ChartKind, true> = { BAR: true, LINE: true };

const DEFAULT_PRESET: PresetChoice = 'TOTAL_SALES';
const DEFAULT_BUCKET: ChartBucket = 'FIVE_MIN';
const DEFAULT_GROUPING: TicketSalesInterval = 'DAY';
const DEFAULT_KIND: ChartKind = 'BAR';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The `nights` value that means "explicitly none ticked" (absent = the upcoming default). */
const NO_NIGHTS = '-';

/**
 * Everything on /charts that describes the chart. The URL carries only what differs from the
 * defaults; a value that fails validation parses as its default.
 */
export type ChartsUrlState = {
  preset: PresetChoice;
  /** Recognition date picked by the user; null = the page's own fallback (newest day with data). */
  date: string | null;
  bucket: ChartBucket;
  windowStart: string | null;
  windowEnd: string | null;
  /** Ticket sales: the ticked nights in picker order; null = the upcoming default, [] = none. */
  nights: string[] | null;
  grouping: TicketSalesInterval;
  type: ChartKind;
  cumulative: boolean;
  split: boolean;
};

function oneOf<T extends string>(set: Record<T, true>, value: string | null): T | null {
  return value !== null && Object.hasOwn(set, value) ? (value as T) : null;
}

function parseNights(value: string | null): string[] | null {
  if (value === null) return null;
  if (value === NO_NIGHTS) return [];
  const ids = [...new Set(value.split(',').map((id) => id.trim().toLowerCase()).filter((id) => UUID_RE.test(id)))];
  return ids.length > 0 ? ids : null;
}

export function parseChartsUrl(params: URLSearchParams): ChartsUrlState {
  const date = params.get('date');
  const from = params.get('from');
  const to = params.get('to');
  return {
    preset: params.get('preset') === 'TICKET_SALES' ? 'TICKET_SALES' : (oneOf(CHART_PRESET_SET, params.get('preset')) ?? DEFAULT_PRESET),
    date: date !== null && DATE_RE.test(date) ? date : null,
    bucket: oneOf(BUCKETS, params.get('bucket')) ?? DEFAULT_BUCKET,
    windowStart: from !== null && TIME_RE.test(from) ? from : null,
    windowEnd: to !== null && TIME_RE.test(to) ? to : null,
    nights: parseNights(params.get('nights')),
    grouping: oneOf(GROUPINGS, params.get('grouping')) ?? DEFAULT_GROUPING,
    type: oneOf(KINDS, params.get('type')) ?? DEFAULT_KIND,
    cumulative: params.get('cumulative') === '1',
    split: params.get('split') === '1',
  };
}

/**
 * The query string for a state: only non-defaults, in a fixed order, and only the keys of the
 * current preset's family (Ticket sales vs the intra-day presets), so switching preset drops the
 * other family's parameters.
 */
export function serializeChartsUrl(state: ChartsUrlState): URLSearchParams {
  const out = new URLSearchParams();
  if (state.preset !== DEFAULT_PRESET) out.set('preset', state.preset);
  if (state.preset === 'TICKET_SALES') {
    if (state.nights !== null) out.set('nights', state.nights.length > 0 ? state.nights.join(',') : NO_NIGHTS);
    if (state.grouping !== DEFAULT_GROUPING) out.set('grouping', state.grouping);
    if (state.type !== DEFAULT_KIND) out.set('type', state.type);
    if (state.cumulative) out.set('cumulative', '1');
    if (state.split) out.set('split', '1');
  } else {
    if (state.date !== null) out.set('date', state.date);
    if (state.bucket !== DEFAULT_BUCKET) out.set('bucket', state.bucket);
    if (state.windowStart !== null) out.set('from', state.windowStart);
    if (state.windowEnd !== null) out.set('to', state.windowEnd);
  }
  return out;
}

/** What `patch` takes: the fields to change, or a function from the current state to them. */
export type ChartsUrlPatch = Partial<ChartsUrlState> | ((current: ChartsUrlState) => Partial<ChartsUrlState>);

/**
 * The chart state held in the query string of /charts. `patch` merges changes into the current
 * state and rewrites the URL in place (`replace`: one history entry however many controls are
 * used). The base of a merge is the URL as it is right now (`window.location`, which the
 * replace updates synchronously), not the last render: the router commits a location change in
 * a transition, so two controls changed within one frame would otherwise merge onto stale state
 * and drop the first change. A change that derives from the current value (ticking a
 * night) passes a function, which receives that fresh state.
 */
export function useChartsUrlState(): { state: ChartsUrlState; patch: (change: ChartsUrlPatch) => void } {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const state = useMemo(() => parseChartsUrl(params), [params]);
  const patch = useCallback(
    (change: ChartsUrlPatch) => {
      const current = parseChartsUrl(new URLSearchParams(window.location.search));
      const partial = typeof change === 'function' ? change(current) : change;
      // Commas and colons stay literal so the link reads `nights=<a>,<b>&from=22:40`.
      const query = serializeChartsUrl({ ...current, ...partial }).toString().replace(/%2C/gi, ',').replace(/%3A/gi, ':');
      navigate({ search: query }, { replace: true });
    },
    [navigate],
  );
  return { state, patch };
}
